// frontend/src/pages/Register.js — Session 19
// Étape 1: Identité + coordonnées (TOUS obligatoires) + particulier/professionnel
// Étape 2: Adresse complète + TVA/société si professionnel
// Admin: supprimé du formulaire public (utiliser create-admin.js côté serveur)
import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import logo from '../assets/logo_original.png';
import theme from '../theme';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// ── Validation belge ──────────────────────────────────────────────────────────
const validate = {
  email:       v => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)            ? '' : 'Email invalide',
  telephone:   v => /^(\+32|0)[0-9]{7,9}$/.test(v.replace(/[\s\-\.]/g,'')) ? '' : 'Format: +32 4XX XX XX XX ou 04XX...',
  code_postal: v => /^[1-9][0-9]{3}$/.test(v)                        ? '' : 'Code postal belge: 4 chiffres',
  numero_tva:  v => /^BE[0-9]{10}$/.test((v||'').toUpperCase())       ? '' : 'Format: BE + 10 chiffres (BE0123456789)',
};

const INIT = {
  nom:'', prenom:'', email:'', telephone:'',
  mot_de_passe:'', confirmPwd:'',
  type_client:'particulier',
  societe:'',
  adresse:'', ville:'', code_postal:'', pays:'Belgique',
  numero_tva:'',
};

// ── Composant champ ───────────────────────────────────────────────────────────
const Field = ({ label, value, onChange, type='text', ph='', required=false, error='', note='' }) => {
  const [showPwd, setShowPwd] = useState(false);
  const isPwd = type === 'password';
  return (
    <div style={{ marginBottom: '16px' }}>
      <label style={S.lbl}>
        {label}{required && <span style={{ color:'#e53e3e' }}> *</span>}
      </label>
      <div style={{ position: 'relative' }}>
        <input
          type={isPwd ? (showPwd ? 'text' : 'password') : type}
          value={value}
          onChange={e => onChange(e.target.value)}
          placeholder={ph}
          style={{
            ...S.inp,
            borderColor: error ? '#e53e3e' : '#dce8f0',
            background: error ? '#fff5f5' : 'white',
            paddingRight: isPwd ? '44px' : '14px',
          }}
        />
        {isPwd && (
          <button type="button" onClick={() => setShowPwd(v => !v)} style={S.eye}>
            {showPwd ? '🙈' : '👁'}
          </button>
        )}
      </div>
      {error && <p style={S.errTxt}>{error}</p>}
      {note  && <p style={S.note}>{note}</p>}
    </div>
  );
};

const Row = ({ children, cols='1fr 1fr' }) => {
  const isMob = window.innerWidth < 768;
  return (
    <div style={{ display:'grid', gridTemplateColumns: isMob ? '1fr' : cols, gap:'14px' }}>
      {children}
    </div>
  );
};

// ── Composant principal ───────────────────────────────────────────────────────
const useIsMobile = () => {
  const [m, setM] = React.useState(() => window.innerWidth < 768);
  React.useEffect(() => {
    const h = () => setM(window.innerWidth < 768);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  return m;
};

const Register = () => {
  const navigate = useNavigate();
  const [step,    setStep]    = useState(1);
  const isMobile = useIsMobile();
  const [form,    setForm]    = useState(INIT);
  const [errors,  setErrors]  = useState({});
  const [loading, setLoading] = useState(false);
  const [errGlobal, setErrGlobal] = useState('');

  const set = (key, val) => {
    let v = val;
    if (key === 'numero_tva') v = val.toUpperCase();
    setForm(p => ({ ...p, [key]: v }));
    if (errors[key]) setErrors(p => ({ ...p, [key]: '' }));
    setErrGlobal('');
  };

  // ── Étape 1 : valider identité + coordonnées ──────────────────────────────
  const handleStep1 = (e) => {
    e.preventDefault();
    const errs = {};
    if (!form.nom.trim())    errs.nom    = 'Nom obligatoire';
    if (!form.prenom.trim()) errs.prenom = 'Prénom obligatoire';
    const eEmail = validate.email(form.email);
    if (eEmail)              errs.email  = eEmail;
    const eTel = validate.telephone(form.telephone);
    if (eTel)                errs.telephone = eTel;
    if (!form.mot_de_passe || form.mot_de_passe.length < 6)
                             errs.mot_de_passe = 'Minimum 6 caractères';
    if (form.mot_de_passe !== form.confirmPwd)
                             errs.confirmPwd = 'Les mots de passe ne correspondent pas';
    if (form.type_client === 'professionnel' && !form.societe.trim())
                             errs.societe = 'Nom de société obligatoire pour un professionnel';
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }
    setStep(2);
  };

  // ── Étape 2 : valider adresse + TVA ──────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = {};
    if (!form.adresse.trim())    errs.adresse    = 'Adresse obligatoire';
    if (!form.ville.trim())      errs.ville      = 'Ville obligatoire';
    const eCP = validate.code_postal(form.code_postal);
    if (eCP)                     errs.code_postal = eCP;
    if (form.type_client === 'professionnel') {
      const eTVA = validate.numero_tva(form.numero_tva);
      if (eTVA)                  errs.numero_tva = eTVA;
    }
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }

    setLoading(true); setErrGlobal('');
    try {
      const res = await axios.post(`${API_URL}/auth/register`, {
        nom: form.nom, prenom: form.prenom, email: form.email,
        mot_de_passe: form.mot_de_passe, telephone: form.telephone,
        role: 'client',   // admin uniquement via script CLI
        adresse: form.adresse, ville: form.ville,
        code_postal: form.code_postal, pays: form.pays,
        numero_tva: form.numero_tva || null,
        type_client: form.type_client,
        societe: form.societe || null,
      });
      if (res.data.success) {
        localStorage.setItem('token', res.data.token);
        localStorage.setItem('user',  JSON.stringify(res.data.data));
        navigate('/dashboard');
      } else {
        setErrGlobal(res.data.message || "Erreur lors de l'inscription");
      }
    } catch (err) {
      setErrGlobal(err.response?.data?.message || 'Erreur de connexion au serveur');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={S.page}>

      {/* Panneau gauche — branding — caché sur mobile */}
      {!isMobile && <div style={S.brand}>
        <img src={logo} alt="MKW Solutions" style={S.brandLogo} />
        <h1 style={S.brandTitle}>MKW Solutions</h1>
        <p style={S.brandSub}>Solutions Énergies &amp; Construction</p>
        <div style={S.brandFeatures}>
          {[
            { icon:'⚡', text:'Gestion complète de vos projets' },
            { icon:'📄', text:'Devis & factures en ligne' },
            { icon:'🔒', text:'Espace client sécurisé' },
            { icon:'📊', text:'Suivi des paiements en temps réel' },
          ].map((f,i) => (
            <div key={i} style={S.brandFeatureRow}>
              <span style={S.brandFeatureIcon}>{f.icon}</span>
              <span style={{ fontSize:'14px', opacity:.9 }}>{f.text}</span>
            </div>
          ))}
        </div>
        <p style={S.brandFooter}>
          Déjà un compte ?{' '}
          <Link to="/login" style={{ color:'white', fontWeight:'700', textDecoration:'underline' }}>
            Se connecter
          </Link>
        </p>
      </div>}

      {/* Bannière mobile compacte */}
      {isMobile && (
        <div style={{ background: 'linear-gradient(135deg,#3d5a75,#4f6d89)', color:'white',
          padding:'16px 20px', display:'flex', alignItems:'center', gap:'12px' }}>
          <img src={logo} alt="MKW" style={{ height:'36px', borderRadius:'6px' }} />
          <div>
            <div style={{ fontWeight:'700', fontSize:'15px' }}>MKW Solutions</div>
            <div style={{ fontSize:'11px', opacity:.8 }}>Créer votre espace client</div>
          </div>
          <Link to="/login" style={{ marginLeft:'auto', color:'white', fontSize:'12px',
            textDecoration:'underline', whiteSpace:'nowrap' }}>
            Connexion
          </Link>
        </div>
      )}

      {/* Panneau droit — formulaire */}
      <div style={S.formPanel}>
        <div style={S.formCard}>

          {/* Indicateur d'étapes */}
          <div style={S.steps}>
            {['Votre identité', 'Votre adresse'].map((label, i) => (
              <React.Fragment key={i}>
                <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:'5px' }}>
                  <div style={{
                    ...S.stepCircle,
                    background: step >= i+1 ? theme.primary : '#e0e0e0',
                    color:      step >= i+1 ? 'white' : '#aaa',
                  }}>
                    {step > i+1 ? '✓' : i+1}
                  </div>
                  <span style={{ fontSize:'11px', fontWeight: step===i+1?'700':'400', color: step===i+1?theme.primary:'#aaa' }}>
                    {label}
                  </span>
                </div>
                {i===0 && (
                  <div style={{ flex:1, height:'2px', background: step===2 ? theme.primary:'#e0e0e0',
                    marginBottom:'22px', maxWidth:'80px' }} />
                )}
              </React.Fragment>
            ))}
          </div>

          {errGlobal && <div style={S.errBox}>⚠️ {errGlobal}</div>}

          {/* ── ÉTAPE 1 : Identité ── */}
          {step === 1 && (
            <form onSubmit={handleStep1} noValidate>
              <h2 style={S.title}>👤 Votre identité</h2>
              <p style={S.sub}>Ces informations apparaîtront sur vos devis et factures.</p>

              <Row>
                <Field label="Nom" value={form.nom} onChange={v=>set('nom',v)}
                  ph="Dupont" required error={errors.nom||''} />
                <Field label="Prénom" value={form.prenom} onChange={v=>set('prenom',v)}
                  ph="Jean" required error={errors.prenom||''} />
              </Row>

              <Field label="Email" type="email" value={form.email} onChange={v=>set('email',v)}
                ph="jean@exemple.be" required error={errors.email||''}
                note="Votre email est votre identifiant de connexion. Il ne pourra pas être modifié." />

              <Field label="Téléphone" type="tel" value={form.telephone} onChange={v=>set('telephone',v)}
                ph="+32 4XX XX XX XX" required error={errors.telephone||''} />

              {/* Type particulier / professionnel */}
              <div style={{ marginBottom:'20px' }}>
                <label style={S.lbl}>Type de compte <span style={{ color:'#e53e3e' }}>*</span></label>
                <div style={{ display:'flex', gap:'10px', marginTop:'6px' }}>
                  {[{v:'particulier',i:'🏠',l:'Particulier'},{v:'professionnel',i:'🏢',l:'Professionnel'}].map(t => (
                    <button key={t.v} type="button" onClick={() => set('type_client', t.v)} style={{
                      flex:1, padding:'11px 8px', cursor:'pointer', fontWeight:'600', fontSize:'13px',
                      border:`2px solid ${form.type_client===t.v ? theme.primary:'#e0e0e0'}`,
                      borderRadius:'8px',
                      background: form.type_client===t.v ? (theme.primaryBg||'#edf2fb') : 'white',
                      color:      form.type_client===t.v ? theme.primary : '#666',
                    }}>{t.i} {t.l}</button>
                  ))}
                </div>
              </div>

              {/* Société — obligatoire si professionnel */}
              {form.type_client === 'professionnel' && (
                <Field label="Nom de la société" value={form.societe} onChange={v=>set('societe',v)}
                  ph="MKW Solutions SA" required error={errors.societe||''} />
              )}

              <Row>
                <Field label="Mot de passe" type="password" value={form.mot_de_passe}
                  onChange={v=>set('mot_de_passe',v)} ph="Min. 6 caractères"
                  required error={errors.mot_de_passe||''} />
                <Field label="Confirmer" type="password" value={form.confirmPwd}
                  onChange={v=>set('confirmPwd',v)} ph="Répétez"
                  required error={errors.confirmPwd||''} />
              </Row>

              <button type="submit" style={S.btn}>Suivant — Votre adresse →</button>
            </form>
          )}

          {/* ── ÉTAPE 2 : Adresse ── */}
          {step === 2 && (
            <form onSubmit={handleSubmit} noValidate>
              <h2 style={S.title}>📋 Votre adresse</h2>
              <p style={S.sub}>Ces coordonnées figureront sur vos factures et devis.</p>

              <Field label="Adresse (rue et numéro)" value={form.adresse} onChange={v=>set('adresse',v)}
                ph="Rue de la Paix 12" required error={errors.adresse||''} />

              <Row cols="2fr 1fr">
                <Field label="Ville" value={form.ville} onChange={v=>set('ville',v)}
                  ph="Bruxelles" required error={errors.ville||''} />
                <Field label="Code postal" value={form.code_postal} onChange={v=>set('code_postal',v)}
                  ph="1000" required error={errors.code_postal||''} />
              </Row>

              <Field label="Pays" value={form.pays} onChange={v=>set('pays',v)} ph="Belgique" />

              {form.type_client === 'professionnel' && (
                <Field label="N° TVA" value={form.numero_tva} onChange={v=>set('numero_tva',v)}
                  ph="BE0123456789" required
                  error={errors.numero_tva||''}
                  note="Format belge: BE suivi de 10 chiffres" />
              )}

              <div style={{ background:'#f0f4f8', borderRadius:'8px', padding:'12px 14px', marginBottom:'20px', fontSize:'13px', color:'#4f6d89' }}>
                📋 Récapitulatif : <strong>{form.prenom} {form.nom}</strong> · {form.email} · {form.type_client}
                {form.societe && ` · ${form.societe}`}
              </div>

              <div style={{ display:'flex', gap:'10px' }}>
                <button type="button" onClick={() => { setStep(1); setErrGlobal(''); }}
                  style={{ ...S.btn, background:'#95a5a6', flex:'0 0 auto', padding:'14px 18px', boxShadow:'none' }}>
                  ← Retour
                </button>
                <button type="submit" disabled={loading}
                  style={{ ...S.btn, flex:1, opacity: loading ? .7 : 1, cursor: loading ? 'not-allowed':'pointer' }}>
                  {loading ? '⏳ Création en cours…' : '✅ Créer mon compte'}
                </button>
              </div>
            </form>
          )}

          <p style={{ textAlign:'center', marginTop:'20px', fontSize:'13px', color:'#aaa' }}>
            Déjà un compte ?{' '}
            <Link to="/login" style={{ color:theme.primary, fontWeight:'600', textDecoration:'none' }}>
              Se connecter
            </Link>
          </p>
          <p style={{ textAlign:'center', marginTop:'14px', fontSize:'11px', color:'#ccc',
            borderTop:`1px solid ${theme.border}`, paddingTop:'12px' }}>
            MKW Solutions · TVA BE1020761286 · mkw-solutions.be
          </p>
        </div>
      </div>
    </div>
  );
};

// ── Styles ────────────────────────────────────────────────────────────────────
const S = {
  page: { display:'flex', flexDirection: window.innerWidth < 768 ? 'column' : 'row', minHeight:'100vh', background: theme.gradientPage||'linear-gradient(135deg,#f0f4f8,#dce8f0)' },
  brand: {
    flex:'0 0 340px', background: theme.gradientPrimary||'linear-gradient(160deg,#3d5a75,#4f6d89)',
    padding:'40px 36px', display:'flex', flexDirection:'column', justifyContent:'center',
    color:'white', position:'sticky', top:0, minHeight:'100vh',
  },
  brandLogo: { width:'180px', height:'auto', borderRadius:'10px', marginBottom:'24px' },
  brandTitle: { margin:'0 0 6px', fontSize:'22px', fontWeight:'800', color:'white' },
  brandSub: { margin:'0 0 32px', fontSize:'13px', opacity:.8 },
  brandFeatures: { display:'flex', flexDirection:'column', gap:'14px', marginBottom:'32px' },
  brandFeatureRow: { display:'flex', alignItems:'center', gap:'12px' },
  brandFeatureIcon: { fontSize:'20px', width:'32px', textAlign:'center' },
  brandFooter: { fontSize:'13px', opacity:.75, marginTop:'auto', paddingTop:'20px' },
  formPanel: { flex:1, display:'flex', alignItems:'flex-start', justifyContent:'center', padding:'clamp(12px,3vw,40px) clamp(12px,3vw,24px)', overflowY:'auto' },
  formCard: { background:'white', borderRadius:'16px', boxShadow:'0 10px 40px rgba(0,0,0,0.1)', padding:'clamp(16px,5vw,36px) 32px', width:'100%', maxWidth:'520px' },
  steps: { display:'flex', alignItems:'center', justifyContent:'center', gap:'12px', marginBottom:'28px' },
  stepCircle: { width:'30px', height:'30px', borderRadius:'50%', display:'flex', alignItems:'center', justifyContent:'center', fontWeight:'700', fontSize:'13px' },
  title: { margin:'0 0 6px', color: theme.textPrimary||'#2c3e50', fontSize:'20px', fontWeight:'700' },
  sub: { margin:'0 0 20px', color:'#666', fontSize:'13px' },
  errBox: { background:'#fff5f5', color:'#e53e3e', border:'1px solid #fed7d7', borderRadius:'8px', padding:'12px 14px', marginBottom:'16px', fontSize:'13px' },
  lbl: { display:'block', fontSize:'13px', fontWeight:'600', color:'#555', marginBottom:'5px' },
  inp: { width:'100%', padding:'10px 14px', border:'1px solid #dce8f0', borderRadius:'8px', fontSize:'14px', outline:'none', color:'#2c3e50', boxSizing:'border-box', display:'block' },
  errTxt: { margin:'3px 0 0', fontSize:'12px', color:'#e53e3e' },
  note: { margin:'3px 0 0', fontSize:'11px', color:'#999' },
  eye: { position:'absolute', right:'10px', top:'50%', transform:'translateY(-50%)', background:'none', border:'none', cursor:'pointer', fontSize:'16px', padding:'2px' },
  btn: { display:'block', width:'100%', padding:'13px', background: theme.gradientPrimary||'linear-gradient(135deg,#4f6d89,#3d5a75)', color:'white', border:'none', borderRadius:'9px', fontSize:'15px', fontWeight:'600', cursor:'pointer', boxShadow:'0 4px 12px rgba(79,109,137,.3)', marginTop:'4px' },
};

export default Register;
